import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useBrand, BrandLogo } from "@/lib/brand";
import { Loader2, Lock, ArrowRight, Calendar } from "lucide-react";
import ABMROICalculator from "@/components/ABMROICalculator";

/**
 * Public ABM landing page — `/abm/:slug`. No auth.
 * Tracks page-view, scroll milestones, and CTA clicks per session.
 */
const SCROLL_BUCKETS = [
  { pct: 25, name: "scroll_25" },
  { pct: 50, name: "scroll_50" },
  { pct: 75, name: "scroll_75" },
  { pct: 100, name: "scroll_100" },
];

export default function ABMPage() {
  const { slug } = useParams();
  const { resolveBy } = useBrand();
  const [page, setPage] = useState(null);
  const [error, setError] = useState("");
  const sessionIdRef = useRef("");
  const firedRef = useRef(new Set());

  if (!sessionIdRef.current) {
    sessionIdRef.current = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `abm-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  const track = async (event) => {
    if (firedRef.current.has(event)) return;
    firedRef.current.add(event);
    try {
      await axios.post(`${API}/abm/${slug}/event`, {
        session_id: sessionIdRef.current,
        event,
      });
    } catch { /* swallow */ }
  };

  useEffect(() => {
    let alive = true;
    resolveBy("abm", slug);
    (async () => {
      try {
        const r = await axios.get(`${API}/abm/${slug}`);
        if (!alive) return;
        setPage(r.data);
        track("view");
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "ABM page not found");
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (!page) return undefined;
    const onScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      if (total <= 0) return;
      const pct = Math.min(100, (scrolled / total) * 100);
      for (const { pct: bucket, name } of SCROLL_BUCKETS) {
        if (pct >= bucket - 1) track(name);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const onCta = async (e) => {
    e.preventDefault();
    await track("cta_click");
    if (page?.cta_url) window.open(page.cta_url, "_blank", "noopener,noreferrer");
  };

  const trackRoiView = () => track("roi_view");
  const trackRoiChange = async ({ revenue, leakPct, computed }) => {
    try {
      await axios.post(`${API}/abm/${slug}/event`, {
        session_id: sessionIdRef.current,
        event: "roi_change",
        roi_revenue: revenue,
        roi_leak_pct: leakPct,
        roi_computed: computed,
      });
    } catch { /* swallow */ }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-8 text-center max-w-md">
          <Lock className="w-7 h-7 text-[#FF3B30] mx-auto mb-3" />
          <div className="font-heading font-bold text-xl mb-2">Page not found</div>
          <div className="font-mono text-[11px] text-zinc-400">{error}</div>
        </div>
      </div>
    );
  }
  if (!page) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-zinc-500 font-mono text-[11px] gap-2">
        <Loader2 className="w-4 h-4 animate-spin text-[#FFB000]" /> LOADING…
      </div>
    );
  }

  const wtSrc = page.walkthrough_id && page.walkthrough_share_token
    ? `${API}/walkthroughs/${page.walkthrough_id}/video?k=${encodeURIComponent(page.walkthrough_share_token)}`
    : null;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="abm-page">
      {/* TOP STRIP — WORKSPACE BRAND × ACCOUNT */}
      <header className="border-b border-zinc-900 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BrandLogo size="md" />
          <span className="text-zinc-700">×</span>
          {page.account_logo_url ? (
            <img src={page.account_logo_url} alt={page.account_name} className="h-7 max-w-[140px] object-contain" />
          ) : (
            <span className="font-heading font-black text-lg">{page.account_name}</span>
          )}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">// PRIVATE WORKING BOARD</div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12 space-y-10">
        {/* HERO */}
        <section data-testid="abm-hero">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">// PREPARED FOR {page.account_name.toUpperCase()}</div>
          <h1 className="font-heading font-black text-5xl md:text-6xl tracking-tighter leading-[1.05] mt-3">
            {page.headline}
          </h1>
          {page.subhead && (
            <p className="text-lg text-zinc-300 mt-5 max-w-3xl leading-relaxed">{page.subhead}</p>
          )}
          <button
            onClick={onCta}
            data-testid="abm-cta-top"
            style={{ backgroundColor: "var(--brand-accent, #FFB000)", color: "var(--brand-accent-text, #000)" }}
            className="inline-flex items-center gap-2 mt-7 font-bold font-mono text-[12px] uppercase tracking-widest px-6 py-3.5 hover:opacity-90 transition-opacity"
          >
            <Calendar className="w-3.5 h-3.5" /> {page.cta_label} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </section>

        {/* WALKTHROUGH (if attached) */}
        {wtSrc && (
          <section data-testid="abm-walkthrough">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">// WALKTHROUGH FOR YOU</div>
            <video
              key={wtSrc}
              controls
              src={wtSrc}
              onPlay={() => track("play")}
              className="w-full bg-zinc-950 border border-zinc-900"
              data-testid="abm-video"
            />
          </section>
        )}

        {/* BODY COPY */}
        <section data-testid="abm-body" className="prose prose-invert max-w-none">
          {page.body.split(/\n\n+/).map((para, i) => (
            <p key={i} className="text-zinc-300 leading-relaxed text-base mb-4">{para}</p>
          ))}
        </section>

        {/* ROI CALCULATOR (optional) */}
        {page.roi_enabled && (
          <ABMROICalculator
            page={page}
            onView={trackRoiView}
            onEngage={trackRoiChange}
          />
        )}

        {/* BOTTOM CTA */}
        <section className="border border-zinc-900 p-8 bg-gradient-to-br from-zinc-950 to-black text-center" data-testid="abm-bottom-cta">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">// NEXT STEP</div>
          <h2 className="font-heading font-black text-3xl tracking-tight mt-2">Let&apos;s map this onto your real numbers.</h2>
          <p className="text-sm text-zinc-400 mt-2 max-w-2xl mx-auto">
            30 minutes, your team + ours, your data on screen. We&apos;ll leave you with a quantified revenue plan whether or not we work together.
          </p>
          <button
            onClick={onCta}
            data-testid="abm-cta-bottom"
            style={{ backgroundColor: "var(--brand-accent, #FFB000)", color: "var(--brand-accent-text, #000)" }}
            className="inline-flex items-center gap-2 mt-5 font-bold font-mono text-[12px] uppercase tracking-widest px-6 py-3.5 hover:opacity-90 transition-opacity"
          >
            {page.cta_label} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </section>

        {/* OWNER FOOTER */}
        <footer className="text-center font-mono text-[10px] text-zinc-600 pt-6">
          Sent by {page.owner_name} · {page.owner_email}
        </footer>
      </main>
    </div>
  );
}
